const SUPPORTED_INLINE_TAGS = ["strong", "b", "em", "i", "code", "mark"];

const INLINE_TAG_REGEX = new RegExp(
  `<(${SUPPORTED_INLINE_TAGS.join("|")})(\\s[^>]*)?>([\\s\\S]*?)</\\1>`,
  "gi",
);

const INLINE_WRAPPERS: Record<string, [string, string]> = {
  strong: ["**", "**"],
  b: ["**", "**"],
  em: ["*", "*"],
  i: ["*", "*"],
  code: ["`", "`"],
  mark: ["==", "=="],
};

function convertInlineTags(html: string): string {
  let result = html;
  let previous = "";

  while (result !== previous) {
    previous = result;
    result = result.replace(INLINE_TAG_REGEX, (_, tag, _attrs, content) => {
      const normalizedTag = tag.toLowerCase();
      const wrapper = INLINE_WRAPPERS[normalizedTag];
      if (!wrapper) return content;
      return wrapper[0] + convertInlineTags(content) + wrapper[1];
    });
  }

  return result;
}

const BR_REGEX = /<br\s*\/?>/gi;
const P_OPEN_REGEX = /<p(\s[^>]*)?>/gi;
const P_CLOSE_REGEX = /<\/p>/gi;

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  nbsp: " ",
  quot: '"',
  "#39": "'",
};

const ENTITY_REGEX = /&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g;

function decodeEntities(text: string): string {
  return text.replace(ENTITY_REGEX, (match, entity: string) => {
    if (entity.startsWith("#x")) {
      return String.fromCharCode(parseInt(entity.slice(2), 16));
    }
    if (entity.startsWith("#")) {
      return String.fromCharCode(parseInt(entity.slice(1), 10));
    }
    return NAMED_ENTITIES[entity] ?? match;
  });
}

const SUPPORTED_TAGS = [
  "strong",
  "b",
  "em",
  "i",
  "code",
  "mark",
  "ul",
  "ol",
  "li",
  "br",
  "p",
];

const UNSUPPORTED_TAG_REGEX = new RegExp(
  `<(?!/?(?:${SUPPORTED_TAGS.join("|")})\\b)[a-zA-Z][^>]*>[\\s\\S]*?</[a-zA-Z][^>]*>`,
  "gi",
);

const ORPHANED_CLOSE_TAG_REGEX = new RegExp(
  `</(?!(?:${SUPPORTED_TAGS.join("|")})\\b)[a-zA-Z][^>]*>`,
  "gi",
);

function removeUnsupportedTags(html: string): string {
  let result = html;
  let previous = "";

  // Iterate to handle nested unsupported tags (inner removed first, then outer)
  while (result !== previous) {
    previous = result;
    result = result.replace(UNSUPPORTED_TAG_REGEX, "");
  }

  // Remove any orphaned closing tags left by nested removal
  result = result.replace(ORPHANED_CLOSE_TAG_REGEX, "");

  return result;
}

const UL_REGEX = /<ul(\s[^>]*)?>[\s\S]*?<\/ul>/gi;
const OL_REGEX = /<ol(\s[^>]*)?>[\s\S]*?<\/ol>/gi;
const LI_REGEX = /<li(\s[^>]*)?>[\s\S]*?<\/li>/gi;
const LI_CONTENT_REGEX = /^<li(\s[^>]*)?>|<\/li>$/gi;

function convertList(listHtml: string, ordered: boolean): string {
  const items = listHtml.match(LI_REGEX);
  if (!items) return "";

  return items
    .map((item, index) => {
      const content = convertInlineTags(
        item.replace(LI_CONTENT_REGEX, "").trim(),
      );
      return ordered ? `${index + 1}. ${content}` : `- ${content}`;
    })
    .join("\n");
}

function convertLists(html: string): string {
  let result = html;
  result = result.replace(UL_REGEX, (match) => convertList(match, false));
  result = result.replace(OL_REGEX, (match) => convertList(match, true));
  return result;
}

/**
 * Converts an HTML string to the markdown subset supported by markdownFormatter.
 *
 * Supported tags: strong, b, em, i, code, mark, ul, ol, li, br, p.
 * Unsupported tags and their content are dropped entirely.
 * HTML entities are decoded to plain text.
 *
 * @param value - The HTML string to convert
 * @returns The equivalent markdown string, or empty string for null/undefined/empty input
 */
export function htmlToMarkdown(value?: string | null): string {
  if (value == null || value === "") {
    return "";
  }

  let result = value;

  // Remove unsupported tags and their content (first, before any conversion)
  result = removeUnsupportedTags(result);

  // Convert <p> tags: strip opening/closing, add newline after closing
  result = result.replace(P_OPEN_REGEX, "");
  result = result.replace(P_CLOSE_REGEX, "\n");

  // Convert lists
  result = convertLists(result);

  // Convert <br> variants to newlines
  result = result.replace(BR_REGEX, "\n");

  // Convert inline tags
  result = convertInlineTags(result);

  // Decode HTML entities (last, so entities inside tags are preserved during matching)
  result = decodeEntities(result);

  // Trim trailing newlines produced by </p> at end of input
  result = result.replace(/\n+$/, "");

  return result;
}
